import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Button, SwitchToggle } from '../../../ui/atoms';

const WidgetSecurity = ({ widgetConfig, onToggleStatus, onConfigChange, onSave, saving }) => {
  const { t } = useTranslation();
  const [isEditingCdn, setIsEditingCdn] = useState(false);

  const handleSaveCdn = async () => {
    await onSave();
    setIsEditingCdn(false);
  };

  if (!widgetConfig?.id) {
    return null;
  }

  return (
    <div className="space-y-6 mb-8">
      <div className="border border-gray-200 rounded-lg p-6 bg-white">
        <h3 className="text-xl font-medium text-gray-900 mb-4">{t('widgets.config.installation.title')}</h3>
        <div className="flex items-start gap-4">
          <SwitchToggle
            checked={widgetConfig.status === 'published'}
            onChange={onToggleStatus}
            label={t('widgets.config.installation.status.current')}
            className="mt-0.5"
          />
          <div>
            <p className="font-medium text-gray-900">
              {t('widgets.config.installation.status.current')}:{' '}
              {widgetConfig.status === 'published'
                ? t('widgets.config.installation.status.active')
                : t('widgets.config.installation.status.inactive')}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {widgetConfig.status === 'published'
                ? t('widgets.config.installation.status.activeDesc')
                : t('widgets.config.installation.status.inactiveDesc')}
            </p>
          </div>
        </div>
      </div>

      <div
        className={`group border border-gray-200 rounded-lg bg-white transition-colors ${
          !isEditingCdn ? 'hover:bg-gray-50 cursor-pointer' : ''
        }`}
        onClick={!isEditingCdn ? () => setIsEditingCdn(true) : undefined}
      >
        <div className="p-6 pb-4 relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-medium text-gray-900">{t('widgets.config.security.images.title')}</h3>
            {!isEditingCdn && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <PencilIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>

          {!isEditingCdn ? (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('widgets.config.security.images.cdnLabel')}</label>
              <p className="text-sm text-gray-900 break-all">{widgetConfig.cdn_images_url || '-'}</p>
              <p className="text-xs text-gray-500 mt-1">
                {t('widgets.config.security.images.description')}
              </p>
            </div>
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              <label htmlFor="cdn_images_url" className="block text-sm font-medium text-gray-700 mb-1">
                {t('widgets.config.security.images.cdnLabel')}
              </label>
              <input
                id="cdn_images_url"
                name="cdn_images_url"
                type="text"
                className="form-input-inset"
                value={widgetConfig.cdn_images_url || ''}
                onChange={onConfigChange}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('widgets.config.security.images.inputHelp')}
              </p>
            </div>
          )}
        </div>

        {isEditingCdn && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
            <Button type="button" variant="secondary" size="md" onClick={() => setIsEditingCdn(false)}>
              {t('widgets.config.actions.cancel')}
            </Button>
            <Button type="button" onClick={handleSaveCdn} disabled={saving} variant="primary" size="md">
              {saving ? t('widgets.config.actions.saving') : t('widgets.config.actions.save')}
            </Button>
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg bg-white">
        <div className="p-6 pb-4 relative">
          <div className="mb-4">
            <h3 className="text-xl font-medium text-gray-900">{t('widgets.config.security.context.title')}</h3>
          </div>
          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('widgets.config.security.context.autoDetectDomains.title')}</p>
                <p className="text-xs text-gray-500">
                  {t('widgets.config.security.context.autoDetectDomains.description')}
                </p>
              </div>
              <SwitchToggle
                checked={Boolean(widgetConfig.auto_detect_domains)}
                onChange={(checked) =>
                  onConfigChange({ target: { name: 'auto_detect_domains', value: checked } })
                }
                label={t('widgets.config.security.context.autoDetectDomains.title')}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('widgets.config.security.context.extractPageContext.title')}</p>
                <p className="text-xs text-gray-500">
                  {t('widgets.config.security.context.extractPageContext.description')}
                </p>
              </div>
              <SwitchToggle
                checked={Boolean(widgetConfig.extract_page_context)}
                onChange={(checked) =>
                  onConfigChange({ target: { name: 'extract_page_context', value: checked } })
                }
                label={t('widgets.config.security.context.extractPageContext.title')}
              />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <Button type="button" onClick={onSave} disabled={saving} variant="primary" size="md">
            {saving ? t('widgets.config.actions.saving') : t('widgets.config.actions.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WidgetSecurity;
